import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  GooglePlayLogo,
  Star,
  CircleNotch,
  PaperPlaneTilt,
  Sparkle,
  ArrowsClockwise,
  ArrowLeft,
  ChatCircleDots,
  TrendUp,
  TrendDown,
  Warning,
  ChatTeardropDots,
  CheckCircle,
  X,
  Smiley,
  SmileyMeh,
  SmileySad,
  AndroidLogo,
  DeviceMobile,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STAR_GRADIENT = {
  1: "from-rose-500 to-red-600",
  2: "from-orange-500 to-amber-600",
  3: "from-yellow-400 to-amber-500",
  4: "from-lime-500 to-green-500",
  5: "from-emerald-500 to-teal-500",
};

const STAR_TINT = {
  1: "#EF4444",
  2: "#F97316",
  3: "#EAB308",
  4: "#84CC16",
  5: "#10B981",
};

function StarRow({ rating, size = 14 }) {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="star-row">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          weight={i <= r ? "fill" : "regular"}
          color={i <= r ? STAR_TINT[r] || "#FBBF24" : "#9CA3AF"}
        />
      ))}
    </div>
  );
}

function timeAgo(ts) {
  if (!ts) return "";
  const seconds = typeof ts === "number" ? ts : Number(ts);
  if (!seconds) return "";
  const d = new Date(seconds * 1000);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function moodFor(rating) {
  if (rating >= 4) return { Icon: Smiley, label: "Happy", color: "#10B981" };
  if (rating === 3) return { Icon: SmileyMeh, label: "Neutral", color: "#F59E0B" };
  return { Icon: SmileySad, label: "Unhappy", color: "#EF4444" };
}

export default function PlayReviews() {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ configured: false, packages: [] });
  const [packageFilter, setPackageFilter] = useState(""); // "" = all packages
  const [installs, setInstalls] = useState({});  // pkg -> {installs_text, installs_min}
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | 1..5 | unanswered | answered
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [posting, setPosting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (["1", "2", "3", "4", "5"].includes(filter)) params.rating = Number(filter);
      if (filter === "unanswered") params.has_reply = false;
      if (filter === "answered") params.has_reply = true;
      if (packageFilter) params.package = packageFilter;
      const [statusR, statsR, listR] = await Promise.all([
        api.get("/play/status"),
        api.get("/play/reviews/stats", { params: packageFilter ? { package: packageFilter } : {} }),
        api.get("/play/reviews", { params: { ...params, limit: 200 } }),
      ]);
      setStatus(statusR.data);
      setStats(statsR.data);
      setItems(listR.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [filter, packageFilter]);

  // Fetch install counts per package (cached server-side for 1hr)
  useEffect(() => {
    if (!status.configured) return;
    const pkgs = (status.packages || []).map((p) => p.name);
    if (pkgs.length === 0) return;
    pkgs.forEach((pkg) => {
      api
        .get("/play/installs", { params: { package: pkg } })
        .then((r) => setInstalls((prev) => ({ ...prev, [pkg]: r.data })))
        .catch(() => {});
    });
  }, [status]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/play/sync", null, {
        params: packageFilter ? { package: packageFilter } : {},
      });
      toast.success(`Synced ${r.data.synced} review(s)`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const aiSuggest = async (reviewId) => {
    setAiBusy(true);
    try {
      const r = await api.post(`/play/reviews/${reviewId}/ai-suggest`);
      setReplyText(r.data.suggestion || "");
      toast.success("AI suggestion ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const postReply = async (reviewId) => {
    if (!replyText.trim()) return toast.error("Reply text empty");
    if (replyText.length > 350) return toast.error("Max 350 characters");
    setPosting(true);
    try {
      await api.post(`/play/reviews/${reviewId}/reply`, { reply_text: replyText });
      toast.success("Reply posted to Google Play");
      setActiveId(null);
      setReplyText("");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setPosting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (r) =>
        (r.text || "").toLowerCase().includes(q) ||
        (r.author_name || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const total = stats?.total || 0;
  const avg = stats?.average || 0;
  const ratingBars = [5, 4, 3, 2, 1].map((r) => ({
    rating: r,
    count: stats?.by_rating?.[r] || 0,
    pct: total ? ((stats?.by_rating?.[r] || 0) / total) * 100 : 0,
  }));

  return (
    <div
      className="min-h-screen bg-[var(--bg)] text-[var(--fg)]"
      data-testid="play-reviews-page"
    >
      {/* Hero header */}
      <header className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--surface)]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => navigate("/")}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#34A853] to-[#1B7E3D] grid place-items-center text-white shadow-md shadow-emerald-500/30">
            <GooglePlayLogo weight="fill" size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="heading text-lg md:text-xl font-bold leading-tight flex items-center gap-2">
              Play Store Reviews
              {(() => {
                const activePkg = packageFilter
                  || (status.packages?.[0]?.name)
                  || status.package_name;
                return activePkg ? (
                  <Badge variant="outline" className="text-[10px] font-mono ml-1">
                    {activePkg}
                  </Badge>
                ) : null;
              })()}
            </div>
            <div className="text-xs text-[var(--muted)] leading-tight">
              Sync, reply &amp; auto-draft AI responses for app reviews on Google
              Play.
            </div>
          </div>
          <Button
            size="sm"
            variant="default"
            onClick={triggerSync}
            disabled={syncing || !status.configured}
            className="h-8 gap-1.5 bg-[#34A853] hover:bg-[#2d8e46]"
            data-testid="sync-btn"
          >
            {syncing ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <ArrowsClockwise size={14} weight="bold" />
            )}
            <span className="hidden md:inline">Sync now</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* App switcher (visible only when 2+ apps configured) */}
        {(status.packages || []).length > 1 && (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
            <button
              onClick={() => setPackageFilter("")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                packageFilter === ""
                  ? "bg-[#34A853] text-white border-[#34A853]"
                  : "border-[var(--border)] hover:bg-white/5"
              }`}
              data-testid="app-filter-all"
            >
              All apps
            </button>
            {status.packages.map((p) => (
              <button
                key={p.name}
                onClick={() => setPackageFilter(p.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                  packageFilter === p.name
                    ? "bg-[#34A853] text-white border-[#34A853]"
                    : "border-[var(--border)] hover:bg-white/5"
                }`}
                data-testid={`app-filter-${p.name}`}
              >
                {p.label}
                <span className="ml-1.5 text-[10px] opacity-70 font-mono">
                  {p.name.split(".").pop()}
                </span>
              </button>
            ))}
          </div>
        )}

        {!status.configured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-900/20 p-4 mb-5 flex items-start gap-3">
            <Warning size={20} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
            <div className="text-sm">
              <div className="font-semibold mb-0.5">Setup pending</div>
              <p className="text-xs text-[var(--muted)]">
                Add <code className="px-1 rounded bg-black/10">GOOGLE_PLAY_PACKAGE_NAME</code>{" "}
                and <code className="px-1 rounded bg-black/10">GOOGLE_PLAY_CREDENTIALS_BASE64</code>{" "}
                to backend env, then redeploy. Service account must also have{" "}
                <strong>Reply to reviews</strong> permission in Play Console.
              </p>
            </div>
          </div>
        )}

        {status.configured && (
          <>
            {/* Stat cards row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <button
                onClick={async () => {
                  const activePkg = packageFilter || (status.packages?.[0]?.name);
                  if (!activePkg) {
                    toast.error("Select an app first");
                    return;
                  }
                  const current = installs[activePkg]?.installs_min || "";
                  const v = window.prompt(
                    `Enter exact install count for ${activePkg} (from Play Console).\nLeave blank to clear override and fall back to public scrape.`,
                    current,
                  );
                  if (v === null) return;  // cancelled
                  try {
                    if (v.trim() === "") {
                      await api.post("/play/installs/override", {
                        package: activePkg,
                        clear: true,
                      });
                      toast.success("Override cleared");
                    } else {
                      const installs_min = parseInt(v.replace(/[,\s]/g, ""), 10);
                      if (Number.isNaN(installs_min) || installs_min < 0) {
                        toast.error("Enter a valid non-negative integer");
                        return;
                      }
                      await api.post("/play/installs/override", {
                        package: activePkg,
                        installs_min,
                      });
                      toast.success("Install count updated");
                    }
                    // Refresh installs
                    const r = await api.get("/play/installs", {
                      params: { package: activePkg },
                    });
                    setInstalls((prev) => ({ ...prev, [activePkg]: r.data }));
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to update");
                  }
                }}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 relative overflow-hidden text-left hover:border-blue-400 transition group"
                data-testid="stat-installs"
              >
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br from-blue-300/30 to-cyan-400/10" />
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1">
                  Total downloads
                  <span className="text-blue-500 opacity-0 group-hover:opacity-100 transition text-[9px] normal-case tracking-normal font-normal ml-auto">
                    click to set exact
                  </span>
                </div>
                <div className="text-3xl font-bold heading mt-1 flex items-center gap-1.5">
                  {(() => {
                    const pkgs = packageFilter
                      ? [packageFilter]
                      : (status.packages || []).map((p) => p.name);
                    const total = pkgs.reduce(
                      (sum, p) => sum + (installs[p]?.installs_min || 0),
                      0,
                    );
                    if (total >= 1_000_000)
                      return `${(total / 1_000_000).toFixed(1)}M+`;
                    if (total >= 1_000)
                      return `${Math.round(total / 1_000)}K+`;
                    if (total > 0) return total.toLocaleString();
                    return "—";
                  })()}
                  <DeviceMobile size={20} className="text-blue-500" weight="fill" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">
                  {(() => {
                    const activePkg = packageFilter || (status.packages?.[0]?.name);
                    const src = installs[activePkg]?.source;
                    if (src === "manual") return "Manual (Play Console exact)";
                    return "Live from Play Store";
                  })()}
                </div>
              </button>

              <div
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 relative overflow-hidden"
                data-testid="stat-average"
              >
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br from-yellow-300/30 to-amber-400/10" />
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  Average rating
                </div>
                <div className="text-3xl font-bold heading mt-1 flex items-center gap-1.5">
                  {avg.toFixed(2)}
                  <Star size={20} weight="fill" color="#FBBF24" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">
                  out of 5 · {total} review{total === 1 ? "" : "s"}
                </div>
              </div>

              <div
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 relative overflow-hidden"
                data-testid="stat-positive"
              >
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br from-emerald-300/30 to-teal-400/10" />
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  Positive (4-5★)
                </div>
                <div className="text-3xl font-bold heading mt-1 flex items-center gap-1.5">
                  {(stats?.by_rating?.[5] || 0) + (stats?.by_rating?.[4] || 0)}
                  <TrendUp size={20} className="text-emerald-500" weight="bold" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">
                  {total
                    ? Math.round(
                        (((stats?.by_rating?.[5] || 0) +
                          (stats?.by_rating?.[4] || 0)) /
                          total) *
                          100,
                      )
                    : 0}
                  % of total
                </div>
              </div>

              <div
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 relative overflow-hidden"
                data-testid="stat-negative"
              >
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br from-rose-300/30 to-red-400/10" />
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  Negative (1-2★)
                </div>
                <div className="text-3xl font-bold heading mt-1 flex items-center gap-1.5">
                  {(stats?.by_rating?.[1] || 0) + (stats?.by_rating?.[2] || 0)}
                  <TrendDown size={20} className="text-rose-500" weight="bold" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">Needs attention</div>
              </div>

              <div
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 relative overflow-hidden"
                data-testid="stat-awaiting"
              >
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br from-orange-300/30 to-rose-400/10" />
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  Awaiting reply
                </div>
                <div className="text-3xl font-bold heading mt-1 flex items-center gap-1.5">
                  {stats?.awaiting_reply || 0}
                  <ChatTeardropDots size={18} className="text-orange-500" weight="fill" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">
                  {total - (stats?.awaiting_reply || 0)} replied
                </div>
              </div>
            </div>

            {/* Distribution + Filters */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
              <div className="lg:col-span-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
                <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-3">
                  Rating distribution
                </div>
                <div className="space-y-2">
                  {ratingBars.map(({ rating, count, pct }) => (
                    <button
                      key={rating}
                      onClick={() => setFilter(String(rating))}
                      className="w-full flex items-center gap-3 group"
                      data-testid={`rating-bar-${rating}`}
                    >
                      <div className="w-12 text-xs flex items-center gap-0.5 shrink-0">
                        <span className="font-semibold">{rating}</span>
                        <Star size={11} weight="fill" color={STAR_TINT[rating]} />
                      </div>
                      <div className="flex-1 h-3 rounded-full bg-black/5 dark:bg-white/5 overflow-hidden relative">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${STAR_GRADIENT[rating]} transition-all duration-500 group-hover:opacity-80`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-10 text-right text-xs text-[var(--muted)] tabular-nums">
                        {count}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 flex flex-col">
                <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-3">
                  Quick filters
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { k: "all", label: "All", color: "default" },
                    { k: "unanswered", label: "Awaiting", color: "destructive" },
                    { k: "answered", label: "Replied", color: "secondary" },
                  ].map(({ k, label }) => (
                    <Button
                      key={k}
                      size="sm"
                      variant={filter === k ? "default" : "outline"}
                      className={`h-7 text-xs ${
                        filter === k && k === "unanswered"
                          ? "bg-rose-500 hover:bg-rose-600"
                          : ""
                      } ${filter === k && k === "answered" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                      onClick={() => setFilter(k)}
                      data-testid={`filter-${k}`}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <div className="mt-3 relative">
                  <input
                    type="text"
                    placeholder="Search reviews..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-md border border-[var(--border)] bg-transparent outline-none focus:border-[#34A853] transition"
                    data-testid="search-input"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--fg)]"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Reviews list */}
            <div className="space-y-3" data-testid="reviews-list">
              {loading && (
                <div className="py-16 grid place-items-center text-[var(--muted)]">
                  <CircleNotch size={32} className="animate-spin" />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="py-20 text-center text-[var(--muted)] text-sm rounded-xl border border-dashed border-[var(--border)] bg-[var(--surface)]">
                  <ChatCircleDots size={40} className="mx-auto mb-2 opacity-40" />
                  {items.length === 0 ? (
                    <>
                      <div>No reviews to show.</div>
                      <div className="text-[11px] mt-2 max-w-md mx-auto">
                        Click <strong>Sync now</strong> to fetch the latest. Note —
                        Google Play API only returns reviews modified in the{" "}
                        <strong>last 7 days</strong>; older reviews stay on the Play
                        Store but are not available here.
                      </div>
                    </>
                  ) : (
                    <>No reviews match these filters.</>
                  )}
                </div>
              )}
              {!loading &&
                filtered.map((rv) => {
                  const isActive = activeId === rv.review_id;
                  const hasReply = !!rv.developer_reply;
                  const mood = moodFor(rv.rating);
                  return (
                    <article
                      key={rv.review_id}
                      className={`group rounded-xl border bg-[var(--surface)] transition-all hover:shadow-md ${
                        rv.rating <= 2 && !hasReply
                          ? "border-rose-300/60 dark:border-rose-800"
                          : "border-[var(--border)]"
                      }`}
                      data-testid="review-card"
                    >
                      <div className="p-4 flex gap-3">
                        <div
                          className={`h-10 w-10 rounded-full grid place-items-center shrink-0 bg-gradient-to-br ${STAR_GRADIENT[rv.rating] || "from-gray-400 to-gray-500"} text-white`}
                        >
                          <span className="font-bold text-sm">
                            {(rv.author_name || "?").charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <div className="font-semibold text-sm heading flex items-center gap-2 flex-wrap">
                                {rv.author_name}
                                <span
                                  className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                  style={{
                                    background: `${mood.color}1A`,
                                    color: mood.color,
                                  }}
                                >
                                  <mood.Icon size={11} weight="fill" /> {mood.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <StarRow rating={rv.rating} size={13} />
                                <span className="text-[11px] text-[var(--muted)]">
                                  {timeAgo(rv.created_ts)}
                                </span>
                                {rv.app_version && (
                                  <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                                    <DeviceMobile size={9} className="mr-0.5" />v{rv.app_version}
                                  </Badge>
                                )}
                                {rv.android_version && (
                                  <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                                    <AndroidLogo size={9} className="mr-0.5" weight="fill" />
                                    Android {rv.android_version}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {hasReply && (
                              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 text-[10px]">
                                <CheckCircle size={10} className="mr-1" weight="fill" /> Replied
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm mt-2 whitespace-pre-wrap break-words leading-relaxed">
                            {rv.text || (
                              <span className="italic text-[var(--muted)]">(No review text)</span>
                            )}
                          </p>

                          {hasReply && (
                            <div className="mt-3 ml-1 pl-3 border-l-2 border-emerald-500/40 text-[12px]">
                              <div className="font-semibold text-emerald-600 dark:text-emerald-400 text-[11px] mb-0.5">
                                RodBez team replied
                              </div>
                              <p className="whitespace-pre-wrap break-words text-[var(--muted)]">
                                {rv.developer_reply.text}
                              </p>
                            </div>
                          )}

                          {!hasReply && !isActive && (
                            <div className="mt-3 flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setActiveId(rv.review_id);
                                  setReplyText("");
                                }}
                                className="h-7 bg-[#34A853] hover:bg-[#2d8e46] gap-1.5"
                                data-testid={`reply-open-${rv.review_id}`}
                              >
                                <PaperPlaneTilt size={12} weight="fill" /> Reply
                              </Button>
                              {rv.rating <= 2 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setActiveId(rv.review_id);
                                    setReplyText("");
                                    aiSuggest(rv.review_id);
                                  }}
                                  className="h-7 gap-1.5 border-[#A78BFA] text-[#7C3AED]"
                                  data-testid={`ai-quick-${rv.review_id}`}
                                >
                                  <Sparkle size={12} weight="fill" /> AI reply
                                </Button>
                              )}
                            </div>
                          )}

                          {isActive && (
                            <div className="mt-3 space-y-2 rounded-lg border border-[var(--border)] bg-[var(--bg)] p-3">
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value.slice(0, 350))}
                                placeholder="Type your reply (max 350 characters)..."
                                rows={3}
                                className="resize-none text-sm bg-[var(--surface)]"
                                data-testid={`reply-textarea-${rv.review_id}`}
                              />
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] text-[var(--muted)] mr-auto tabular-nums">
                                  {replyText.length}/350
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => aiSuggest(rv.review_id)}
                                  disabled={aiBusy}
                                  className="gap-1.5 h-7 border-[#A78BFA] text-[#7C3AED]"
                                  data-testid={`ai-suggest-${rv.review_id}`}
                                >
                                  {aiBusy ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <Sparkle size={12} weight="fill" />
                                  )}
                                  AI suggest
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setActiveId(null);
                                    setReplyText("");
                                  }}
                                  className="h-7"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => postReply(rv.review_id)}
                                  disabled={posting || !replyText.trim()}
                                  className="bg-[#34A853] hover:bg-[#2d8e46] h-7 gap-1.5"
                                  data-testid={`reply-send-${rv.review_id}`}
                                >
                                  {posting ? (
                                    <CircleNotch size={12} className="animate-spin" />
                                  ) : (
                                    <PaperPlaneTilt size={12} weight="fill" />
                                  )}
                                  Send
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
